<script setup>
import Grid from "../../../../frontend/src/components/Grid.vue";
import useGrid from "@/composables/useGrid";
import {computed, onMounted, ref} from "vue";
import partnershipsService from "@/services/partnerships.service";
import PartnershipEdit from "@/components/partnerships/PartnershipEdit.vue";
import {useRoute} from "vue2-helpers/vue-router";
import {download} from "../../../../frontend/src/helpers/file";

const props = defineProps({
    forModal: {
        type: Boolean,
        default: false,
    },
    subsectorId: {
        type: Number,
        default: 0,
    }
});

const {items, gridParams, gridMethods} = useGrid(getListMethod);
const route = useRoute();

const columns = [
    {
        label: 'ID',
        name: 'id',
        filter: 'text',
    },
    {
        label: 'Corporate',
        name: 'corporate_name',
        filter: 'text',
    },
    {
        label: 'Startup ID',
        name: 'startup_id',
        filter: 'text',
        component: 'Link',
    },
    {
        label: 'Startup Name',
        name: 'startup_name',
        filter: 'text',
    },
    {
        label: 'Startup URL',
        name: 'startup_website',
        filter: 'text',
        component: 'Link',
    },
    {
        label: 'Sector',
        name: 'sector_name',
        filter: 'text',
    },
    {
        label: 'Subsector',
        name: 'subsector_name',
        filter: 'text',
    },
    {
        label: 'Industry',
        name: 'industry',
        filter: 'text',
    },
    {
        label: 'Partnership Year',
        name: 'partnership_year',
        filter: 'text',
        sortable: true,
    },
    {
        label: 'Summary',
        name: 'summary',
        filter: 'text',
    },
    {
        label: 'Source',
        name: 'source',
        filter: 'text',
        component: 'Link',
    },
    {
        label: 'Population Source',
        name: 'population_source',
        filter: 'single-select',
        options: [
            {
                id: 'subsectors',
                name: 'subsectors'
            },
            {
                id: 'corporates',
                name: 'corporates'
            },
            {
                id: 'admin',
                name: 'admin'
            },
        ]
    },
    {
        label: 'LLM Model',
        name: 'llm_model',
        filter: 'single-select',
        options: [
            {
                id: 'gpt-4o',
                name: 'gpt-4o'
            },
            {
                id: 'perplexity',
                name: 'perplexity'
            },
        ]
    },
    {
        label: 'Created At',
        name: 'created_at',
        component: 'Date',
        sortable: true,
    },
    {
        name: 'actions',
        label: 'Actions',
        component: 'Actions',
    },
];

const editPartnership = ref(null);

const actualColumns = computed(() => {
    if (props.forModal) {
        return columns.filter(item => !['sector_name', 'subsector_name', 'industry'].includes(item.name));
    }

    return columns;
});

onMounted(() => {
    gridMethods.setColumns(actualColumns);
    gridMethods.resetList();
});

function reset() {
    gridMethods.resetList();
}

function getListMethod(pageParams) {
    if (props.subsectorId) {
        pageParams.filters.subsector_id = props.subsectorId;
    }

    if (route.query.corporate_id) {
        pageParams.filters.corporate_id = route.query.corporate_id;
    }

    return partnershipsService.getIndexList(pageParams);
}

function gridAction(params) {
    if (params.action === 'edit') {
        editPartnership.value.open(params.rowId);
    } else if (params.action === 'delete') {
        remove(params.rowId);
    }
}

async function remove(id) {
    if (confirm("Delete partnership?")) {
        await partnershipsService.delete(id);
        gridMethods.updateList();
    }
}

async function exportRecords() {
    let exportColumns = columns.filter(
        item => [
            'id',
            'corporate_name',
            'startup_id',
            'startup_name',
            'startup_website',
            'sector_name',
            'subsector_name',
            'industry',
            'partnership_year',
            'summary',
        ].includes(item.name)
    );

    exportColumns.splice(2, 0, {
        name: 'corporate_url',
        label: 'Corporate Domain',
    });

    const {data} = await partnershipsService.exportRecords({
        columns: exportColumns.map(item => item.name),
        filters: gridParams.selectedAllResults ? gridParams.filters : {'id': gridParams.selectedRows},
        exportColumns: exportColumns,
    });

    download(data, 'partnerships.csv');
}

defineExpose({
    reset,
})
</script>

<template>
    <div>
        <div v-if="!forModal" class="topbar pl-sm-5">
            <div class="topbar__inner">
                <div class="topbar__key">
                    <header>
                        <button class="button w-100 w-sm-auto" @click="editPartnership.open()">Add Partnership</button>
                        <button
                            v-if="gridParams.selectedRows.length"
                            class="button w-100 w-sm-auto ml-2"
                            @click="exportRecords"
                        >
                            Export
                        </button>
                    </header>
                </div>
            </div>
        </div>

        <div class="main-table-wrapper">
            <div class="main-table overflow-auto">
                <Grid
                    :columns="actualColumns"
                    :data="items"
                    :has-checkbox-column="!forModal"
                    has-filters
                    :page-length="parseInt(gridParams.pageLength)"
                    :total-rows-count="gridParams.totalRowsCount"
                    :sort="gridParams.sort"
                    :currentPage="gridParams.currentPage"
                    @changePage="gridMethods.changePage"
                    @changeFilters="gridMethods.setFilters"
                    @changeSort="gridMethods.changeSort"
                    @selectedRowsChanged="gridMethods.changeSelectedRows"
                    @selectAllResults="gridMethods.selectAllResults"
                    @action="gridAction"
                />
            </div>
        </div>

        <PartnershipEdit
            ref="editPartnership"
            @saved="gridMethods.updateList"
        />
    </div>
</template>

<style scoped>

</style>
